import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useHistory, Redirect } from 'react-router-dom';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
  DragOverlay,
} from '@dnd-kit/core';

import useUserStore from '../stores/useUserStore';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import LogoSpinner from '../components/ui/LogoSpinner';

import WeekNav from './WeekNav';
import WeekNotesCard from './WeekNotesCard';
import AirspaceStatsCard from './AirspaceStatsCard';
import DayColumn, { PilotEntry } from './DayColumn';
import { POAItemModel } from './POAItem';
import ViewToggle, { POAViewMode } from './ViewToggle';
import FocusGhost from './FocusGhost';

import { weekStartFor, dateForDay, DAY_LABELS_SHORT } from '../utils/poa-helpers';
import PageLayout from '../components/ui/PageLayout';
import Reveal from '../components/ui/Reveal';
import { FadeIn } from '../components/ui/FadeIn';

const VIEW_MODE_STORAGE_KEY = 'firefleet.poa.view';

/**
 * Compute today's day index (1=Mon..7=Sun) under UTC, or null if `weekStart`
 * isn't the current week. Pulled out so the focus-reset effect and the
 * render-time "today highlight" share the same definition.
 */
function todayDayFor(weekStart: string): number | null {
  if (weekStart !== weekStartFor()) return null;
  const dow = new Date().getUTCDay(); // 0=Sun..6=Sat
  return dow === 0 ? 7 : dow;
}

interface POAResponse {
  week_start: string;
  week_notes: { body: string; updated_at: string | null; updated_by: string | null };
  items: POAItemModel[];
  pilots_by_date: Record<string, PilotEntry[]>;
}

const POAPage: React.FC = () => {
  const user = useUserStore(s => s.user);
  const api = useApi();
  const toast = useToast();
  const location = useLocation();
  const history = useHistory();

  // Hooks must be declared before any early return — viewAllowed gates the
  // render path further down, not the hook execution.
  const viewAllowed = !!user && (user.can_pilot || user.can_manager || user.can_manufacturing || user.can_admin || user.isadmin);
  const isManager = !!user && (user.can_manager || user.can_admin || user.isadmin);
  // Manufacturing gets the POA but not airspace authorization context.
  const showAirspace = !!user && (user.can_pilot || user.can_manager || user.can_admin || user.isadmin);

  // URL → state for week navigation
  const params = new URLSearchParams(location.search);
  const initialWeek = weekStartFor(params.get('week') || undefined);
  const [weekStart, setWeekStart] = useState(initialWeek);

  // View mode (Grid | Agenda) — persisted across sessions
  const [viewMode, setViewMode] = useState<POAViewMode>(() => {
    if (typeof window === 'undefined') return 'grid';
    return localStorage.getItem(VIEW_MODE_STORAGE_KEY) === 'agenda' ? 'agenda' : 'grid';
  });
  useEffect(() => {
    try { localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode); } catch { /* quota / private mode */ }
  }, [viewMode]);

  // Focused day (1..7). On a current week, defaults to today. On other
  // weeks, defaults to Monday. The user can click any day's promote button
  // to override; the override is transient — switching weeks resets the
  // default. Keeping it out of URL state keeps deep-links simple.
  const [focusDay, setFocusDay] = useState<number>(() => todayDayFor(initialWeek) ?? 1);
  useEffect(() => {
    setFocusDay(todayDayFor(weekStart) ?? 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart]);

  const [data, setData] = useState<POAResponse | null>(null);
  const [loading, setLoading] = useState(true);

  // Sequence-id guard so a slow earlier fetch can't clobber a newer one when
  // the user spams week-nav. Each call bumps the ref; only the latest write
  // is allowed to land.
  const fetchSeqRef = React.useRef(0);
  const fetchWeek = useCallback(async (w: string) => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    try {
      const resp: POAResponse = await api.get(`/poa?week_start=${w}`);
      if (seq !== fetchSeqRef.current) return;
      setData(resp);
    } catch (e: any) {
      if (seq !== fetchSeqRef.current) return;
      toast.error('Failed to load Plan of Activities');
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, [api, toast]);

  useEffect(() => {
    if (!viewAllowed) return;
    fetchWeek(weekStart);
    // sync URL
    const next = new URLSearchParams();
    next.set('week', weekStart);
    history.replace({ pathname: '/pilots', search: `?${next.toString()}` });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart, viewAllowed]);

  // ---------- Mutations ----------

  const handleCreate = async (day: number, body: string, isCompletable: boolean) => {
    try {
      const inserted: POAItemModel = await api.post('/poa/items', {
        body: { week_start: weekStart, day, body, is_completable: isCompletable },
      });
      setData(d => d ? { ...d, items: [...d.items, inserted] } : d);
    } catch (e: any) {
      toast.error('Could not add item');
    }
  };

  const handleUpdate = async (id: string, fields: { body?: string; is_completable?: boolean }) => {
    try {
      const updated: POAItemModel = await api.patch(`/poa/items/${id}`, { body: fields });
      setData(d => d ? { ...d, items: d.items.map(it => it.id === id ? updated : it) } : d);
    } catch (e: any) {
      toast.error('Could not update item');
    }
  };

  const handleComplete = async (id: string, completed: boolean) => {
    try {
      const updated: POAItemModel = await api.post(`/poa/items/${id}/complete`, { body: { completed } });
      setData(d => d ? { ...d, items: d.items.map(it => it.id === id ? updated : it) } : d);
    } catch (e: any) {
      toast.error('Could not toggle completion');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/poa/items/${id}`);
      setData(d => d ? { ...d, items: d.items.filter(it => it.id !== id) } : d);
    } catch (e: any) {
      toast.error('Could not delete item');
    }
  };

  const handleSaveNotes = async (body: string) => {
    try {
      const row = await api.put('/poa/week-notes', { body: { week_start: weekStart, body } });
      setData(d => d ? { ...d, week_notes: { body: row.body, updated_at: row.updated_at, updated_by: row.updated_by } } : d);
      toast.success('Notes saved');
    } catch (e: any) {
      toast.error('Could not save notes');
    }
  };

  // ---------- Drag & drop ----------

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    if (!data) return;
    const { active, over } = event;
    if (!over) return;

    const activeItem = data.items.find(it => it.id === active.id);
    if (!activeItem) return;

    // Determine destination day + index
    let destDay = activeItem.day;
    let destIndex: number | null = null;

    if ((over.data.current as any)?.type === 'day') {
      destDay = (over.data.current as any).day;
      // Drop into empty day → place at end
      destIndex = data.items.filter(i => i.day === destDay && i.id !== active.id).length;
    } else if ((over.data.current as any)?.type === 'item') {
      destDay = (over.data.current as any).day;
      const sameDayItems = data.items
        .filter(i => i.day === destDay)
        .sort((a, b) => a.position - b.position);
      const overIndex = sameDayItems.findIndex(i => i.id === over.id);
      destIndex = overIndex === -1 ? sameDayItems.length : overIndex;
    }

    if (destIndex == null) return;
    if (destDay === activeItem.day && destIndex === activeItem.position) return;

    // Optimistic local move
    const before = data.items;
    const sourceDayItems = before.filter(i => i.day === activeItem.day && i.id !== active.id).sort((a, b) => a.position - b.position);
    const destDayItems = destDay === activeItem.day
      ? sourceDayItems
      : before.filter(i => i.day === destDay && i.id !== active.id).sort((a, b) => a.position - b.position);
    const newDest = [...destDayItems];
    const moving = { ...activeItem, day: destDay };
    newDest.splice(Math.max(0, Math.min(destIndex, newDest.length)), 0, moving);

    const optimistic: POAItemModel[] = before
      .filter(i => i.id !== active.id)
      .map(i => {
        if (i.day === activeItem.day && i.day !== destDay) {
          // re-pack source day
          const idx = sourceDayItems.findIndex(s => s.id === i.id);
          return idx === -1 ? i : { ...i, position: idx };
        }
        if (i.day === destDay) {
          const idx = newDest.findIndex(s => s.id === i.id);
          return idx === -1 ? i : { ...i, position: idx };
        }
        return i;
      });
    const movingFinalIdx = newDest.findIndex(s => s.id === active.id);
    optimistic.push({ ...moving, position: movingFinalIdx });
    setData({ ...data, items: optimistic });

    // Server-authoritative move. Optimistic state above already mirrors what
    // the server will compute; no refetch on success (a refetch would race
    // a follow-up drag and could clobber the newer optimistic state).
    try {
      await api.post(`/poa/items/${active.id}/move`, {
        body: { day: destDay, position: movingFinalIdx },
      });
    } catch {
      toast.error('Move failed; reverting');
      // Refetch instead of restoring the closed-over `before` snapshot —
      // any create/delete/complete that landed during the move would be
      // wiped if we naïvely setData(before). Server is source of truth.
      fetchWeek(weekStart);
    }
  };

  // ---------- Render ----------

  if (!user) return <LogoSpinner />;
  if (!viewAllowed) return <Redirect to="/" />;

  const todayDay = todayDayFor(weekStart);

  const itemsByDay: Record<number, POAItemModel[]> = {};
  for (let d = 1; d <= 7; d++) itemsByDay[d] = [];
  if (data) {
    for (const it of data.items) {
      if (itemsByDay[it.day]) itemsByDay[it.day].push(it);
    }
    for (let d = 1; d <= 7; d++) {
      itemsByDay[d].sort((a, b) => a.position - b.position);
    }
  }

  return (
    <PageLayout width="full" kind="workspace" className="pt-4 space-y-5">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-primary">Plan of Activities</h1>
          <p className="text-xs text-on-surface-variant mt-0.5">Pilot team — Mon through Sun</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <ViewToggle value={viewMode} onChange={setViewMode} />
          <WeekNav weekStart={weekStart} onChange={setWeekStart} />
        </div>
      </div>

      {/* Top banner: week notes + (pilot-only) airspace stats */}
      <div className={`grid grid-cols-1 gap-4 ${showAirspace ? 'lg:grid-cols-3' : ''}`}>
        <div className={showAirspace ? 'lg:col-span-2' : ''}>
          {data && (
            <FadeIn className="h-full">
              <WeekNotesCard
                body={data.week_notes.body}
                isManager={isManager}
                onSave={handleSaveNotes}
              />
            </FadeIn>
          )}
        </div>
        {showAirspace && (
          <div className="lg:col-span-1">
            <AirspaceStatsCard />
          </div>
        )}
      </div>

      {/* Week layout — grid (focus + 6-col + ghost) or agenda (vertical) */}
      {loading && !data ? (
        <LogoSpinner label="Loading the plan of activities" />
      ) : !data ? null : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <FadeIn key={viewMode}>
          {viewMode === 'grid' ? (
            <div className="space-y-3">
              {/* Focus card — the promoted day, hoisted above the grid. */}
              <DayColumn
                weekStart={weekStart}
                day={focusDay}
                items={itemsByDay[focusDay] || []}
                pilots={data.pilots_by_date[dateForDay(weekStart, focusDay)] || []}
                isManager={isManager}
                size="focus"
                isToday={focusDay === todayDay}
                onUpdate={handleUpdate}
                onComplete={handleComplete}
                onDelete={handleDelete}
                onCreate={handleCreate}
              />

              {/*
                Two explicit flex rows for the 6 non-focus days.
                The thin FocusGhost indicator slots in at the focused day's
                chronological position, between the day cards.

                buildRows() returns Mon-Wed-ish in row 1 and Thu-Sun-ish
                in row 2 (split after 3 day cards), with the indicator
                sitting between adjacent days within whichever row it
                belongs to (or at the end of row 1 if focusDay is between).
              */}
              {(() => {
                const rows: Array<Array<{ kind: 'day' | 'indicator'; day: number }>> = [[], []];
                let currentRow = 0;
                let dayCountInRow = 0;
                for (let d = 1; d <= 7; d++) {
                  if (d === focusDay) {
                    rows[currentRow].push({ kind: 'indicator', day: d });
                  } else {
                    if (dayCountInRow === 3) {
                      currentRow = 1;
                      dayCountInRow = 0;
                    }
                    rows[currentRow].push({ kind: 'day', day: d });
                    dayCountInRow++;
                  }
                }
                return rows.map((row, rowIdx) => (
                  // min-h gives both rows the same baseline; cards stretch to
                  // row height via h-full and excess content scrolls within
                  // each card so neither row balloons past the baseline.
                  <div key={rowIdx} className="flex gap-3 min-h-[240px]">
                    {row.map(cell =>
                      cell.kind === 'indicator' ? (
                        <FocusGhost
                          key={`ghost-${cell.day}`}
                          day={cell.day}
                          isToday={cell.day === todayDay}
                          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        />
                      ) : (
                        <div key={cell.day} className="flex-1 min-w-0 h-auto">
                          <DayColumn
                            weekStart={weekStart}
                            day={cell.day}
                            items={itemsByDay[cell.day] || []}
                            pilots={data.pilots_by_date[dateForDay(weekStart, cell.day)] || []}
                            isManager={isManager}
                            size="compact"
                            isToday={cell.day === todayDay}
                            onPromote={() => setFocusDay(cell.day)}
                            onUpdate={handleUpdate}
                            onComplete={handleComplete}
                            onDelete={handleDelete}
                            onCreate={handleCreate}
                          />
                        </div>
                      )
                    )}
                  </div>
                ));
              })()}
            </div>
          ) : (
            // Agenda mode — vertical stack in chronological order. Focused day
            // stays at its natural date position but renders with focus chrome.
            <div className="space-y-3">
              {[1, 2, 3, 4, 5, 6, 7].map(day => {
                const isFocused = day === focusDay;
                return (
                  <DayColumn
                    key={day}
                    weekStart={weekStart}
                    day={day}
                    items={itemsByDay[day] || []}
                    pilots={data.pilots_by_date[dateForDay(weekStart, day)] || []}
                    isManager={isManager}
                    size={isFocused ? 'focus' : 'agenda'}
                    isToday={day === todayDay}
                    onPromote={isFocused ? undefined : () => setFocusDay(day)}
                    onUpdate={handleUpdate}
                    onComplete={handleComplete}
                    onDelete={handleDelete}
                    onCreate={handleCreate}
                  />
                );
              })}
            </div>
          )}
          </FadeIn>

          {/* DragOverlay placeholder kept minimal — items render in-place via @dnd-kit/sortable transforms */}
          <DragOverlay />
        </DndContext>
      )}

      {/* Footnote: short legend so the day "moved from X" subtitle isn't mysterious */}
      <Reveal show={!!data && data.items.some(i => i.original_day !== i.day)}>
        <div className="text-[10px] text-on-surface-variant px-1">
          Tip: items show "Moved from {DAY_LABELS_SHORT[1]}" when their original day differs.
        </div>
      </Reveal>
    </PageLayout>
  );
};

export default POAPage;
